/**
 * Workshop version of DataFrame.
 */
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { FrameHeader } from "@/workshop/components/shapes/shared/frame-header";
import { FrameFooter } from "@/workshop/components/shapes/shared/frame-footer";

interface DataColumn { header: string; key: string; }
interface DataRow { [key: string]: { value: string; color?: string }; }

interface DataFrameProps {
  title?: string;
  columns?: DataColumn[];
  rows?: DataRow[];
  totalRows?: number;
  agentName?: string;
  onResizeStart?: React.MouseEventHandler;
  className?: string;
  "data-testid"?: string;
}

const MOCK_COLUMNS: DataColumn[] = [
  { header: "Region", key: "region" },
  { header: "Revenue", key: "revenue" },
  { header: "Growth", key: "growth" },
];

const MOCK_ROWS: DataRow[] = [
  { region: { value: "Europe" }, revenue: { value: "$2.4M", color: "text-primary" }, growth: { value: "+18%", color: "text-frame-data" } },
  { region: { value: "Americas" }, revenue: { value: "$3.1M", color: "text-primary" }, growth: { value: "+12%", color: "text-frame-data" } },
  { region: { value: "APAC" }, revenue: { value: "$1.8M", color: "text-primary" }, growth: { value: "+24%", color: "text-frame-data" } },
];

export function DataFrame({
  title = "Revenue by Region",
  columns = MOCK_COLUMNS,
  rows = MOCK_ROWS,
  totalRows = 12,
  agentName = "Analyst",
  onResizeStart,
  className,
  "data-testid": testId,
}: DataFrameProps) {
  return (
    <div className={cn("denker-glass-frame flex flex-col rounded-frame overflow-hidden", className)} data-testid={testId ?? "data-frame"}>
      <FrameHeader title={title} icon={Icons.BarChart2} accentColor="bg-frame-data" agentName={agentName} statusColor="bg-muted" />
      <div className="flex flex-1 flex-col p-1">
        <div className="flex w-full flex-col">
          <div className="flex w-full bg-surface px-1.5 py-1" data-testid="data-header-row">
            {columns.map((col) => (
              <span key={col.key} className="flex-1 font-mono text-canvas-xs font-semibold text-muted">{col.header}</span>
            ))}
          </div>
          {rows.map((row, i) => (
            <div key={i} className="flex w-full border-t border-border px-1.5 py-1" data-testid={`data-row-${i}`}>
              {columns.map((col) => {
                const cell = row[col.key];
                return (
                  <span key={col.key} className={cn("flex-1 font-mono text-canvas-xs", cell?.color ?? "text-secondary")}>{cell?.value ?? ""}</span>
                );
              })}
            </div>
          ))}
        </div>
      </div>
      {/* Row count — shown above footer since workshop FrameFooter doesn't support children */}
      <div className="flex items-center border-t border-glass-stroke-faint px-2 py-0.5">
        <span className="text-canvas-2xs text-muted">{rows.length} of {totalRows} rows</span>
      </div>
      <FrameFooter onResizeStart={onResizeStart} data-testid="data-frame-footer" />
    </div>
  );
}

export type { DataFrameProps, DataColumn, DataRow };
